

import { bootstrap } from '@angular-architects/module-federation-tools';
import { AppModule } from './app/app.module';
import { environment } from './app/environments/environment';

// Initialize Datadog RUM based on environment
async function initializeDatadogRUM(): Promise<void> {
  // Import Datadog RUM SDK
  const { datadogRum } = await import('@datadog/browser-rum');
  
  // Load application configuration (which includes Datadog config)
  const configResponse = await fetch(`./assets/${environment.configFileName}`);
  if (!configResponse.ok) {
    throw new Error(`Failed to load application config: ${environment.configFileName}`);
  }
  
  const appConfig = await configResponse.json();
  const datadogConfig = appConfig?.["datadog"];
  
  // Validate that Datadog configuration exists and has required fields
  if (!datadogConfig?.["applicationId"] || !datadogConfig?.["clientToken"]) {
    console.warn('⚠️ Datadog RUM: Missing or incomplete Datadog configuration in config file. Skipping initialization.');
    return;
  }
  
  // Initialize Datadog RUM with environment-specific configuration
  const rumConfig: any = {
    applicationId: datadogConfig.applicationId,
    clientToken: datadogConfig.clientToken,
    site: datadogConfig.site || 'datadoghq.com',
    service: datadogConfig.service,
    env: datadogConfig.env,
    version: datadogConfig.version,
    sessionSampleRate: Number(datadogConfig.sessionSampleRate),
    sessionReplaySampleRate: Number(datadogConfig.sessionReplaySampleRate),
    trackUserInteractions: datadogConfig.trackUserInteractions,
    trackResources: datadogConfig.trackResources,
    trackLongTasks: datadogConfig.trackLongTasks,
    defaultPrivacyLevel: datadogConfig.defaultPrivacyLevel as 'allow' | 'mask' | 'mask-user-input'
  };

  // Add trackBfcacheViews if specified in config (handles TypeScript compatibility)
  if (datadogConfig.trackBfcacheViews !== undefined) {
    rumConfig.trackBfcacheViews = datadogConfig.trackBfcacheViews;
  }

  datadogRum.init(rumConfig);

  // Start collecting RUM data and session replay
  datadogRum.startSessionReplayRecording();
  
  // Set initial anonymous user context
  // This will be replaced with authenticated user data in app.component.ts after login
  datadogRum.setUser({
    id: 'anonymous',
    name: 'Anonymous User',
    email: 'anonymous@example.com'
  });

  // Add global context
  datadogRum.setGlobalContextProperty('app.name', 'pgm-portal');
  datadogRum.setGlobalContextProperty('app.version', datadogConfig["version"]);
  datadogRum.setGlobalContextProperty('app.environment', datadogConfig["env"]);
}

// Main application startup with better error handling
(async () => {
  // Initialize Datadog with error handling
  try {
    await initializeDatadogRUM();
  } catch (error) {
    console.warn('⚠️ Datadog RUM failed to initialize:', error);
    // App should still work without Datadog
  }
  
  // Bootstrap application
  try {
    await import('./bootstrap');
  } catch (error) {
    console.error('❌ Critical: Failed to bootstrap application:', error);
    // This is a critical error - app won't work
  }
})();

// import { loadManifest } from '@angular-architects/module-federation';

//  loadManifest("./assets/mf.manifest.json")
//    .catch(err => console.error(err))
//    .then(_ => import('./bootstrap'))
//    .catch(err => console.error(err));
